/**
 * Canvas ecran : viewport (pan/zoom), boucle de rendu, conversions de
 * coordonnees. Vit entierement hors de React : aucune de ses mutations ne
 * declenche de re-render.
 *
 * La boucle ne peint que si quelque chose a change (`markDirty`) ou si une
 * animation est en cours : au repos, le cout est un rAF vide.
 */
import {
  type Size,
  type Vec2,
  type Viewport,
  clampViewport,
  ease,
  fitViewport,
  imageToNormalized,
  imageToScreen,
  lerpViewport,
  screenToImage,
} from './geometry.js';
import { MAX_DEVICE_PIXEL_RATIO } from './renderConfig.js';

export type RenderFn = (ctx: CanvasRenderingContext2D, viewport: Viewport, now: number) => void;

interface ViewportAnimation {
  readonly from: Viewport;
  readonly to: Viewport;
  readonly startedAt: number;
  readonly durationMs: number;
  readonly resolve: () => void;
}

export class ArtworkRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly imageSize: Size;

  private viewport: Viewport;
  private viewSize: Size = { width: 1, height: 1 };
  private animation: ViewportAnimation | null = null;

  private renderFn: RenderFn | null = null;
  private rafId = 0;
  private dirty = true;

  constructor(canvas: HTMLCanvasElement, imageSize: Size) {
    const ctx = canvas.getContext('2d', { alpha: true });
    if (ctx === null) throw new Error('contexte 2d indisponible');

    this.canvas = canvas;
    this.ctx = ctx;
    this.imageSize = imageSize;
    this.viewport = { scale: 1, tx: 0, ty: 0 };
    this.resize();
    this.viewport = fitViewport(imageSize, this.viewSize);
  }

  get view(): Size {
    return this.viewSize;
  }

  get image(): Size {
    return this.imageSize;
  }

  getViewport(): Viewport {
    return this.viewport;
  }

  setViewport(viewport: Viewport, clamp = true): void {
    this.viewport = clamp ? clampViewport(viewport, this.imageSize, this.viewSize) : viewport;
    this.markDirty();
  }

  markDirty(): void {
    this.dirty = true;
  }

  /** Recalcule la taille du backing store. A appeler sur resize/rotation. */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);

    const backingWidth = Math.round(width * dpr);
    const backingHeight = Math.round(height * dpr);
    if (this.canvas.width !== backingWidth || this.canvas.height !== backingHeight) {
      this.canvas.width = backingWidth;
      this.canvas.height = backingHeight;
    }

    this.viewSize = { width, height };
    this.markDirty();
  }

  fit(): void {
    this.setViewport(fitViewport(this.imageSize, this.viewSize), false);
  }

  /** Anime le viewport. La promesse se resout a la fin (ou a l'interruption). */
  animateTo(target: Viewport, durationMs: number): Promise<void> {
    this.animation?.resolve();
    if (durationMs <= 0) {
      this.setViewport(target, false);
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.animation = {
        from: this.viewport,
        to: target,
        startedAt: performance.now(),
        durationMs,
        resolve,
      };
      this.markDirty();
    });
  }

  cancelAnimation(): void {
    this.animation?.resolve();
    this.animation = null;
  }

  // -------------------------------------------------------------------------
  // Gestes
  // -------------------------------------------------------------------------

  panBy(dx: number, dy: number): void {
    this.setViewport({ scale: this.viewport.scale, tx: this.viewport.tx + dx, ty: this.viewport.ty + dy });
  }

  /** Zoom autour d'un point ecran (px CSS), typiquement le centre du pinch. */
  zoomAt(factor: number, center: Vec2): void {
    const { scale, tx, ty } = this.viewport;
    const nextScale = scale * factor;
    this.setViewport({
      scale: nextScale,
      tx: center.x - (center.x - tx) * (nextScale / scale),
      ty: center.y - (center.y - ty) * (nextScale / scale),
    });
  }

  // -------------------------------------------------------------------------
  // Coordonnees
  // -------------------------------------------------------------------------

  /** Coordonnees clientX/clientY -> px CSS relatifs au canvas. */
  clientToView(clientX: number, clientY: number): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  /** Coordonnees clientX/clientY -> normalise 0-1 sur l'image. */
  clientToNormalized(clientX: number, clientY: number): Vec2 {
    return this.viewToNormalized(this.clientToView(clientX, clientY));
  }

  /**
   * px CSS relatifs au canvas -> normalise 0-1 sur l'image.
   *
   * Existe pour les appelants qui tiennent DEJA un point en coordonnees de vue
   * (le survol, par exemple) : repasser par `clientToNormalized` les obligerait
   * a reconstituer des coordonnees client, et donc a relire la boite du canvas
   * une seconde fois par mouvement de souris.
   */
  viewToNormalized(view: Vec2): Vec2 {
    return imageToNormalized(screenToImage(view, this.viewport), this.imageSize);
  }

  /** Normalise 0-1 sur l'image -> px CSS relatifs au canvas. */
  normalizedToView(point: Vec2): Vec2 {
    return imageToScreen(
      { x: point.x * this.imageSize.width, y: point.y * this.imageSize.height },
      this.viewport,
    );
  }

  // -------------------------------------------------------------------------
  // Boucle
  // -------------------------------------------------------------------------

  start(renderFn: RenderFn): void {
    this.renderFn = renderFn;
    if (this.rafId === 0) {
      this.rafId = requestAnimationFrame(this.frame);
    }
  }

  stop(): void {
    if (this.rafId !== 0) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.renderFn = null;
    this.cancelAnimation();
  }

  private readonly frame = (now: number): void => {
    this.rafId = requestAnimationFrame(this.frame);

    const animation = this.animation;
    if (animation !== null) {
      const t = Math.min((now - animation.startedAt) / animation.durationMs, 1);
      this.viewport = lerpViewport(animation.from, animation.to, ease(t));
      this.dirty = true;
      if (t >= 1) {
        this.animation = null;
        animation.resolve();
      }
    }

    if (!this.dirty || this.renderFn === null) return;
    this.dirty = false;

    const dpr = this.canvas.width / this.viewSize.width;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.clearRect(0, 0, this.viewSize.width, this.viewSize.height);
    this.renderFn(this.ctx, this.viewport, now);
  };
}
